import { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react';

import { GroupedMultiSelectGroup } from '@components/new/GroupedMultiSelect/GroupedMultiSelect';
import { useOpeningClosingFilters } from '@apiHooks/useOpeningClosingFilters';
import { useRestaurantOpeningsClosings } from '@apiHooks/useRestaurantOpeningsClosings';
import { OpeningsClosingsQuery } from '@apiHooks/openingsClosingsParams.helpers';
import { useTerritorySelection } from '@pages/Home/context/TerritorySelectionContext';
import { MonthlyDatum } from '../RestaurantOpeningsClosings.types';
import { buildFilterModel, selectedToQuery } from '../OpeningsClosingsFilters.helpers';

interface OpeningsClosingsFilterValue {
    filterGroups: GroupedMultiSelectGroup[];
    selectedIds: number[];
    setSelectedIds: (ids: number[]) => void;
    filtersLoading: boolean;
    data: MonthlyDatum[];
    lastMonth: MonthlyDatum | undefined;
    isLoading: boolean;
    isSuccess: boolean | null;
    query: OpeningsClosingsQuery;
}

const OpeningsClosingsFilterContext = createContext<OpeningsClosingsFilterValue | null>(null);

export const OpeningsClosingsFilterProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { savedSelection } = useTerritorySelection();
    const { data: rawFilters, isLoading: filtersLoading } = useOpeningClosingFilters();
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    // buildFilterModel allocates a Map over the full filter list on every call.
    const { groups: filterGroups, idToOption } = useMemo(
        () => buildFilterModel(rawFilters),
        [rawFilters],
    );

    const { segments, cuisines, chainSizes } = useMemo(
        () => selectedToQuery(selectedIds, idToOption),
        [selectedIds, idToOption],
    );

    const query = useMemo<OpeningsClosingsQuery>(
        () => ({ savedSelection, segments, cuisines, chainSizes }),
        [savedSelection, segments, cuisines, chainSizes],
    );

    const { data, isLoading, isSuccess } = useRestaurantOpeningsClosings(query);

    const lastMonth = data[data.length - 1];

    // Prevents consumers re-rendering when the provider's parent re-renders without a value change.
    // setSelectedIds from useState is stable by React contract, no useCallback needed.
    const value = useMemo<OpeningsClosingsFilterValue>(
        () => ({
            filterGroups,
            selectedIds,
            setSelectedIds,
            filtersLoading,
            data,
            lastMonth,
            isLoading,
            isSuccess,
            query,
        }),
        [filterGroups, selectedIds, setSelectedIds, filtersLoading, data, lastMonth, isLoading, isSuccess, query],
    );

    return (
        <OpeningsClosingsFilterContext.Provider value={value}>
            {children}
        </OpeningsClosingsFilterContext.Provider>
    );
};

export const useOpeningsClosingsFilters = (): OpeningsClosingsFilterValue => {
    const ctx = useContext(OpeningsClosingsFilterContext);
    if (ctx === null) {
        throw new Error('useOpeningsClosingsFilters must be used inside OpeningsClosingsFilterProvider');
    }
    return ctx;
};
